import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import {
  ArrowLeft, AlertTriangle, Loader2, ShieldOff, ShieldCheck,
  PlayCircle, Eye, History, RefreshCw, DollarSign, Lock,
} from 'lucide-react';
import { toast } from 'sonner';

const RunOneBookingPanel = ({ candidates = [], onPaid }) => {
  const [selectedId, setSelectedId] = useState('');
  const [bypassDelay, setBypassDelay] = useState(false);
  const [eligibility, setEligibility] = useState(null);
  const [loadingElig, setLoadingElig] = useState(false);
  const [paying, setPaying] = useState(false);
  const [result, setResult] = useState(null);

  const loadEligibility = async (id) => {
    if (!id) { setEligibility(null); return; }
    setLoadingElig(true);
    try {
      const r = await api.get(`/admin/auto-payouts/bookings/${id}/eligibility`);
      setEligibility(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Eligibility check failed');
      setEligibility(null);
    } finally {
      setLoadingElig(false);
    }
  };

  useEffect(() => { loadEligibility(selectedId); }, [selectedId]);

  const onSelect = (id) => { setSelectedId(id); setResult(null); };

  const allGatesPass = eligibility?.all_pass || (
    eligibility && eligibility.gates.every((g) =>
      g.ok || (bypassDelay && g.gate === 'DELAY_WINDOW_PASSED')
    )
  );

  const runLive = async () => {
    if (!selectedId) return;
    const confirm1 = window.confirm(
      `LIVE Stripe transfer for booking ${selectedId.slice(0, 8)} — $${eligibility?.amount_total?.toFixed(2)}. This is REAL money. Continue?`
    );
    if (!confirm1) return;
    const confirm2 = window.prompt(`Type "PAY" to confirm the live transfer of $${eligibility?.amount_total?.toFixed(2)}.`);
    if (confirm2 !== 'PAY') {
      toast.info('Cancelled — confirmation phrase did not match.');
      return;
    }
    setPaying(true);
    try {
      const r = await api.post(
        `/admin/auto-payouts/bookings/${selectedId}/run-one${bypassDelay ? '?bypass_delay_window=true' : ''}`
      );
      setResult(r.data);
      if (r.data.decision === 'paid') {
        toast.success(`Paid $${r.data.amount?.toFixed(2)} · transfer ${r.data.transfer_id?.slice(0, 12)}…`);
        if (onPaid) await onPaid();
      } else if (r.data.decision === 'failed') {
        toast.error(`Failed: ${r.data.reason}`);
      } else {
        toast.warning(`Skipped: ${r.data.reason}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Live transfer failed');
    } finally {
      setPaying(false);
    }
  };

  return (
    <div className="space-y-3">
      <div>
        <Label className="text-xs">Eligible candidates</Label>
        <select
          className="mt-1 w-full border rounded px-2 py-1.5 text-sm"
          value={selectedId}
          onChange={(e) => onSelect(e.target.value)}
          data-testid="auto-payouts-run-one-select"
        >
          <option value="">— choose a booking —</option>
          {candidates.map((c) => (
            <option key={c.booking_id} value={c.booking_id}>
              {c.booking_id.slice(0, 8)}… · ${c.would_pay_amount.toFixed(2)} (hours ${c.hours_pay.toFixed(2)} + reimb ${c.reimbursement.toFixed(2)})
            </option>
          ))}
        </select>
        {candidates.length === 0 && (
          <div className="text-xs text-gray-500 mt-1">
            No candidates from the last dry-run. Click <strong>Dry-run preview</strong> above first.
          </div>
        )}
      </div>

      {selectedId && (
        <div className="rounded border border-gray-200 p-3 bg-gray-50" data-testid="auto-payouts-eligibility-checklist">
          <div className="text-xs font-semibold text-gray-700 mb-2">Eligibility checklist</div>
          {loadingElig
            ? <div className="text-xs text-gray-500"><Loader2 className="w-3 h-3 animate-spin inline mr-1" /> Checking…</div>
            : !eligibility
              ? <div className="text-xs text-gray-500">—</div>
              : (
                <>
                  <ul className="space-y-1 text-xs">
                    {eligibility.gates.map((g) => (
                      <li
                        key={g.gate}
                        className="flex items-start gap-2"
                        data-testid={`auto-payouts-gate-${g.gate}`}
                      >
                        {g.ok
                          ? <span className="text-emerald-600 font-bold">✓</span>
                          : <span className="text-red-600 font-bold">✗</span>}
                        <div className="flex-1">
                          <span className={g.ok ? 'text-gray-800' : 'text-red-700 font-medium'}>{g.gate}</span>
                          {g.hint && <div className="text-[10px] text-gray-500">{g.hint}</div>}
                        </div>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-3 pt-2 border-t border-gray-200 text-xs">
                    Hours pay: <strong>${eligibility.hours_pay.toFixed(2)}</strong> ·
                    {' '}Reimb: <strong>${eligibility.reimbursement.toFixed(2)}</strong> ·
                    {' '}Total: <strong className="text-gray-900">${eligibility.amount_total.toFixed(2)}</strong>
                  </div>
                </>
              )
          }

          <div className="mt-3 flex items-center gap-2">
            <input
              type="checkbox"
              id="bypass-delay"
              checked={bypassDelay}
              onChange={(e) => setBypassDelay(e.target.checked)}
              data-testid="auto-payouts-bypass-delay"
            />
            <Label htmlFor="bypass-delay" className="text-xs">
              Override delay-window gate (only if the admin has manually verified)
            </Label>
          </div>

          <Button
            onClick={runLive}
            disabled={paying || !allGatesPass}
            className="mt-3 bg-red-600 hover:bg-red-700 text-white disabled:bg-gray-300"
            data-testid="auto-payouts-run-one-btn"
          >
            {paying
              ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Transferring…</>
              : <><PlayCircle className="w-4 h-4 mr-1" /> Pay this ONE booking (LIVE)</>}
          </Button>
        </div>
      )}

      {result && (
        <div
          className={`rounded border p-3 ${result.decision === 'paid' ? 'bg-emerald-50 border-emerald-300' : 'bg-red-50 border-red-300'}`}
          data-testid="auto-payouts-run-one-result"
        >
          <div className="text-sm font-semibold mb-1">
            {result.decision === 'paid' ? '✓ Live transfer initiated' : `Skipped / failed: ${result.reason}`}
          </div>
          {result.decision === 'paid' && (
            <div className="text-xs space-y-0.5">
              <div>Booking: <span className="font-mono">{result.booking_id}</span></div>
              <div>Stripe transfer: <span className="font-mono" data-testid="auto-payouts-run-one-transfer-id">{result.transfer_id}</span></div>
              <div>Payout record: <span className="font-mono">{result.payout_id}</span></div>
              <div>Amount: <strong>${result.amount?.toFixed(2)}</strong></div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const AdminAutoPayouts = () => {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [pending, setPending] = useState(null);
  const [audit, setAudit] = useState({ rows: [], skip_reason_glossary: {} });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cfg, pen, aud] = await Promise.all([
        api.get('/admin/auto-payouts/config'),
        api.get('/admin/auto-payouts/pending'),
        api.get('/admin/auto-payouts/audit?limit=25'),
      ]);
      setConfig(cfg.data);
      setPending(pen.data);
      setAudit(aud.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load auto-payouts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateConfig = async (patch) => {
    setBusy(true);
    try {
      const res = await api.patch('/admin/auto-payouts/config', patch);
      setConfig(res.data);
      toast.success('Config updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    } finally {
      setBusy(false);
    }
  };

  const runLive = async () => {
    if (!config?.enabled) { toast.error('Kill switch is OFF — flip it on first.'); return; }
    if (!window.confirm('LIVE Stripe transfers will be issued for every eligible booking. Continue?')) return;
    setBusy(true);
    try {
      const res = await api.post('/admin/auto-payouts/run');
      toast.success(`paid=${res.data.paid_count} · skipped=${res.data.skipped_count} · failed=${res.data.failed_count}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Run failed');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }

  const totalWouldPay = (pending?.dry || []).reduce((s, r) => s + (r.would_pay_amount || 0), 0);

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-4">
      <div className="flex items-center gap-2 mb-2">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/home')} data-testid="auto-payouts-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Home
        </Button>
        <h1 className="text-xl font-bold">Auto Payouts</h1>
        {config?.enabled
          ? <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-300"><ShieldCheck className="w-3 h-3 mr-1" /> ENABLED</Badge>
          : <Badge className="bg-red-100 text-red-700 border border-red-300"><ShieldOff className="w-3 h-3 mr-1" /> DISABLED</Badge>}
      </div>

      <Card className="border-2 border-amber-300 bg-amber-50/40" data-testid="auto-payouts-stripe-warning">
        <CardContent className="p-4 flex items-start gap-3">
          <Lock className="w-5 h-5 text-amber-700 mt-0.5 shrink-0" />
          <div className="text-sm text-amber-900">
            <strong>Stripe is in LIVE mode.</strong> Flipping the kill switch ON will
            schedule REAL transfers every 30 minutes for any booking that meets all
            eligibility gates (clocked-out, hours approved, no flags, onboarded).
            Use the Dry-Run preview below to confirm what *would* be paid before
            enabling.
          </div>
        </CardContent>
      </Card>

      {/* Config */}
      <Card data-testid="auto-payouts-config-card">
        <CardHeader>
          <CardTitle className="text-base">Configuration</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label>Kill Switch</Label>
              <div className="text-xs text-gray-500">Master ON/OFF for the auto-payout scheduler</div>
            </div>
            <Switch
              checked={!!config?.enabled}
              onCheckedChange={(v) => updateConfig({ enabled: v })}
              disabled={busy}
              data-testid="auto-payouts-killswitch"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs">Min hours after clock-out</Label>
              <Input
                type="number" step="0.25" min="0" max="72"
                defaultValue={config?.min_hours_after_clockout}
                onBlur={(e) => updateConfig({ min_hours_after_clockout: parseFloat(e.target.value) })}
                data-testid="auto-payouts-min-hours"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">Max payouts per run</Label>
              <Input
                type="number" min="1" max="200" step="1"
                defaultValue={config?.max_payouts_per_run}
                onBlur={(e) => updateConfig({ max_payouts_per_run: parseInt(e.target.value, 10) })}
                data-testid="auto-payouts-run-cap"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">Max payout amount (USD)</Label>
              <Input
                type="number" min="0" max="10000" step="50"
                defaultValue={config?.max_payout_amount_usd}
                onBlur={(e) => updateConfig({ max_payout_amount_usd: parseFloat(e.target.value) })}
                data-testid="auto-payouts-amount-cap"
                className="mt-1"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Dry-run / Live actions */}
      <Card data-testid="auto-payouts-actions-card">
        <CardHeader>
          <CardTitle className="text-base">Run Now</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <Button onClick={load} variant="outline" disabled={busy} data-testid="auto-payouts-refresh-btn">
              <RefreshCw className="w-4 h-4 mr-1" /> Refresh
            </Button>
            <Button onClick={async () => { setBusy(true); try { const res = await api.get('/admin/auto-payouts/pending'); setPending(res.data); toast.success('Dry-run preview refreshed'); } finally { setBusy(false); } }} disabled={busy} data-testid="auto-payouts-dry-run-btn">
              <Eye className="w-4 h-4 mr-1" /> Dry-run preview
            </Button>
            <Button onClick={runLive} className="bg-red-600 hover:bg-red-700 text-white" disabled={busy || !config?.enabled} data-testid="auto-payouts-live-run-btn" title={config?.enabled ? 'Issue REAL Stripe transfers now' : 'Enable kill switch first'}>
              <PlayCircle className="w-4 h-4 mr-1" /> Run Live (Stripe)
            </Button>
          </div>
          <div className="rounded border border-gray-200 p-3 bg-gray-50">
            <div className="text-xs text-gray-600 mb-2">Would pay this run:</div>
            <div className="text-2xl font-bold text-gray-900" data-testid="auto-payouts-would-pay">
              ${totalWouldPay.toFixed(2)}
            </div>
            <div className="text-xs text-gray-500 mt-0.5">
              candidates: {pending?.candidates ?? 0} · would-pay rows: {pending?.dry?.length ?? 0}
            </div>
            {pending?.dry?.length > 0 && (
              <div className="mt-3 max-h-60 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="text-gray-500">
                    <tr>
                      <th className="text-left py-1">Booking</th>
                      <th className="text-right py-1">Hours pay</th>
                      <th className="text-right py-1">Reimb.</th>
                      <th className="text-right py-1">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {pending.dry.map((r) => (
                      <tr key={r.booking_id} data-testid={`auto-payouts-dry-row-${r.booking_id}`}>
                        <td className="py-1 font-mono text-[10px]">{r.booking_id.slice(0, 8)}</td>
                        <td className="py-1 text-right">${r.hours_pay.toFixed(2)}</td>
                        <td className="py-1 text-right">${r.reimbursement.toFixed(2)}</td>
                        <td className="py-1 text-right font-semibold">${r.would_pay_amount.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Run for One Booking (LIVE) — controlled go-live */}
      <Card data-testid="auto-payouts-run-one-card" className="border-2 border-emerald-300">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <PlayCircle className="w-4 h-4 text-emerald-700" />
            Run for One Booking (LIVE)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="text-xs text-gray-600">
            Pick ONE eligible booking, verify the gates, and fire a single
            real Stripe transfer. No simulation, no scheduler — controlled
            go-live so you can confirm the first live payout end-to-end
            before flipping the global kill switch.
          </div>

          <RunOneBookingPanel
            candidates={pending?.dry || []}
            onPaid={async () => { await load(); }}
          />
        </CardContent>
      </Card>

      {/* Audit log */}
      <Card data-testid="auto-payouts-audit-card">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><History className="w-4 h-4" /> Recent Decisions</CardTitle>
        </CardHeader>
        <CardContent>
          {audit.rows.length === 0 ? (
            <div className="text-xs text-gray-500 py-4 text-center">No auto-payout decisions yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-gray-500">
                  <tr>
                    <th className="text-left py-1">When</th>
                    <th className="text-left py-1">Booking</th>
                    <th className="text-left py-1">Decision</th>
                    <th className="text-left py-1">Reason</th>
                    <th className="text-right py-1">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {audit.rows.map((r) => (
                    <tr key={r.id} data-testid={`auto-payouts-audit-row-${r.id}`}>
                      <td className="py-1 whitespace-nowrap text-gray-500">
                        {new Date(r.created_at).toLocaleString()}
                      </td>
                      <td className="py-1 font-mono text-[10px]">{r.booking_id.slice(0, 8)}</td>
                      <td className="py-1">
                        <Badge className={
                          r.decision === 'paid' ? 'bg-emerald-100 text-emerald-700' :
                          r.decision === 'failed' ? 'bg-red-100 text-red-700' :
                          'bg-gray-100 text-gray-700'
                        }>{r.decision}</Badge>
                      </td>
                      <td className="py-1 text-gray-700">{r.reason}</td>
                      <td className="py-1 text-right font-semibold">
                        {r.amount != null ? `$${r.amount.toFixed(2)}` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminAutoPayouts;
